import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import { DEFAULT_VAT_RATE, calcPriceInclVat, calcPriceExclVat, formatPrice } from '../../utils/vat';
import DecimalField from '../shared/DecimalField';
import { durationMinutesToHours, durationHoursToMinutes } from '../../utils/laborTime';
import { useUserStore } from '../../store';
import VatModeToggle from '../shared/VatModeToggle';
import VatConverseLine from '../shared/VatConverseLine';

const priceTypeOptions = [
  { value: 'fixed', label: 'Fixed price' },
  { value: 'hourly', label: 'Hourly rate' },
  { value: 'variable', label: 'Variable' },
];

const JobFormDialog = ({
  t,
  open,
  onClose,
  job,
  onJobCreated,
  onJobUpdated,
  onCategoryCreated,
  categories = [],
}) => {
  const { currentOrganization } = useUserStore();
  const orgDefaultVat = currentOrganization?.default_price_includes_vat ?? false;
  const isEditMode = Boolean(job);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [priceIncludesVat, setPriceIncludesVat] = useState(orgDefaultVat);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    cost_price: '',
    vat_rate: DEFAULT_VAT_RATE,
    job_code: '',
    category: null,
    estimated_duration: '',
    price_type: 'fixed',
    is_quick_select: false,
  });

  useEffect(() => {
    if (job) {
      // Restore VAT preference and display appropriate price
      const wasEnteredWithVat = job.price_entered_with_vat || false;
      const priceToShow = wasEnteredWithVat && job.stored_price_incl_vat
        ? job.stored_price_incl_vat
        : job.price || '';

      setFormData({
        name: job.name || '',
        description: job.description || '',
        price: priceToShow,
        cost_price: job.cost_price || '',
        vat_rate: parseFloat(job.vat_rate) || DEFAULT_VAT_RATE,
        job_code: job.job_code || '',
        category: job.category || null,
        // Stored in minutes, edited in decimal hours ("1,5")
        estimated_duration: job.estimated_duration ? String(durationMinutesToHours(job.estimated_duration)) : '',
        price_type: job.price_type || 'fixed',
        is_quick_select: !!job.is_quick_select,
      });
      // Restore VAT toggle state from stored preference
      setPriceIncludesVat(wasEnteredWithVat);
    } else {
      setFormData({
        name: '',
        description: '',
        price: '',
        cost_price: '',
        vat_rate: DEFAULT_VAT_RATE,
        job_code: '',
        category: null,
        estimated_duration: '',
        price_type: 'fixed',
        is_quick_select: false,
      });
      setPriceIncludesVat(orgDefaultVat);
    }
    setError(null);
    setShowNewCategory(false);
    setNewCategoryName('');
  }, [job, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCategoryChange = (event, newValue) => {
    if (newValue && newValue.inputValue) {
      // User typed a new category name
      setShowNewCategory(true);
      setNewCategoryName(newValue.inputValue);
    } else {
      setFormData((prev) => ({
        ...prev,
        category: newValue?.id || null,
      }));
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;

    try {
      const response = await axios.post('/api/job-categories/', {
        name: newCategoryName.trim(),
      });
      const newCategory = response.data;
      if (onCategoryCreated) {
        onCategoryCreated(newCategory);
      }
      setFormData((prev) => ({
        ...prev,
        category: newCategory.id,
      }));
      setShowNewCategory(false);
      setNewCategoryName('');
    } catch (err) {
      console.error('Error creating category:', err);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError(t('Job name is required'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Calculate the price (convert from VAT-inclusive to VAT-exclusive if needed)
      let priceValue = formData.price ? parseFloat(formData.price) : 0;
      if (priceValue && priceIncludesVat) {
        priceValue = calcPriceExclVat(priceValue, formData.vat_rate);
      }

      const payload = {
        ...formData,
        price: priceValue,
        vat_rate: parseFloat(formData.vat_rate),
        // Save VAT preference for toggle restoration
        stored_price_incl_vat: priceIncludesVat ? parseFloat(formData.price) : null,
        price_entered_with_vat: priceIncludesVat,
        estimated_duration: durationHoursToMinutes(formData.estimated_duration),
        category: formData.category || null,
        cost_price: formData.cost_price ? parseFloat(formData.cost_price) : null,
      };

      let response;
      if (isEditMode) {
        response = await axios.patch(`/api/jobs/${job.id}/`, payload);
        if (onJobUpdated) {
          onJobUpdated(response.data);
        }
      } else {
        response = await axios.post('/api/jobs/', payload);
        if (onJobCreated) {
          onJobCreated(response.data);
        }
      }

      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedCategory = categories.find((c) => c.id === formData.category) || null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
          {isEditMode ? t('Edit Job') : t('Add Job')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Name and Code row */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <TextField
              name="name"
              label={t('Job Name')}
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              error={Boolean(error && !formData.name.trim())}
              sx={{
                flex: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />
            <TextField
              name="job_code"
              label={t('Job Code')}
              value={formData.job_code}
              onChange={handleChange}
              placeholder="e.g., OIL-01"
              fullWidth
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />
          </Box>

          <TextField
            name="description"
            label={t('Description')}
            value={formData.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={2}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />

          {/* Category */}
          {showNewCategory ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                label={t('New Category Name')}
                fullWidth
                autoFocus
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                  },
                }}
              />
              <Button
                variant="contained"
                onClick={handleCreateCategory}
                sx={{ borderRadius: '10px', px: 3 }}
              >
                {t('Add')}
              </Button>
              <Button
                onClick={() => {
                  setShowNewCategory(false);
                  setNewCategoryName('');
                }}
                sx={{ borderRadius: '10px' }}
              >
                {t('Cancel')}
              </Button>
            </Box>
          ) : (
            <Autocomplete
              value={selectedCategory}
              onChange={handleCategoryChange}
              options={categories}
              getOptionLabel={(option) => option.name || ''}
              filterOptions={(options, params) => {
                const filtered = options.filter((option) =>
                  option.name.toLowerCase().includes(params.inputValue.toLowerCase())
                );

                if (params.inputValue !== '' && !filtered.some((o) => o.name.toLowerCase() === params.inputValue.toLowerCase())) {
                  filtered.push({
                    inputValue: params.inputValue,
                    name: `${t('Add')} "${params.inputValue}"`,
                  });
                }

                return filtered;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoComplete="off"
                  label={t('Category')}
                  placeholder={t('Select or create category...')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  {option.inputValue ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AddIcon fontSize="small" color="primary" />
                      <Typography>{option.name}</Typography>
                    </Box>
                  ) : (
                    option.name
                  )}
                </li>
              )}
              freeSolo
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
            />
          )}

          {/* Price type and Price row */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <FormControl sx={{ minWidth: 140, flexShrink: 0 }}>
              <InputLabel>{t('Price Type')}</InputLabel>
              <Select
                name="price_type"
                value={formData.price_type}
                onChange={handleChange}
                label={t('Price Type')}
                sx={{ borderRadius: '10px' }}
              >
                {priceTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DecimalField
                  label={
                    formData.price_type === 'hourly'
                      ? (priceIncludesVat ? t('Hourly Rate (incl. VAT)') : t('Hourly Rate (excl. VAT)'))
                      : (priceIncludesVat ? t('Price (incl. VAT)') : t('Price (excl. VAT)'))
                  }
                  value={formData.price}
                  onChange={(value) => setFormData((prev) => ({ ...prev, price: value }))}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {formData.price_type === 'hourly' ? `€/${t('h')}` : '€'}
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                    },
                  }}
                />
                <VatModeToggle
                  value={priceIncludesVat ? 'incl' : 'excl'}
                  onChange={(e, val) => val && setPriceIncludesVat(val === 'incl')}
                  sx={{ flexShrink: 0 }}
                />
              </Box>
              {/* Show calculated price */}
              <VatConverseLine
                value={formData.price}
                mode={priceIncludesVat ? 'incl' : 'excl'}
                vatRate={formData.vat_rate}
                suffix={formData.price_type === 'hourly' && `/${t('h')}`}
              />
            </Box>
          </Box>

          {/* Cost price for margin calculation */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <DecimalField
              label={
                formData.price_type === 'hourly'
                  ? t('Cost price (per hour) (excl. VAT)')
                  : t('Cost price (excl. VAT)')
              }
              value={formData.cost_price}
              onChange={(value) => setFormData((prev) => ({ ...prev, cost_price: value }))}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {formData.price_type === 'hourly' ? `€/${t('h')}` : '€'}
                  </InputAdornment>
                ),
              }}
              helperText={t('costPriceHelperText')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />
            {formData.cost_price && (
              <Typography sx={{ fontSize: '12px', color: 'text.secondary', ml: 1 }}>
                {t('Price incl. VAT')}: {formatPrice(calcPriceInclVat(formData.cost_price, formData.vat_rate))} €
                {formData.price_type === 'hourly' && `/${t('h')}`}
              </Typography>
            )}
          </Box>

          {/* Estimated duration */}
          <DecimalField
            name="estimated_duration"
            label={t('Estimated Duration')}
            value={formData.estimated_duration}
            onChange={(value) => setFormData((prev) => ({ ...prev, estimated_duration: value }))}
            InputProps={{
              endAdornment: <InputAdornment position="end">{t('laborTime.unit')}</InputAdornment>,
            }}
            inputProps={{ 'data-testid': 'job-estimated-duration' }}
            helperText={formData.price_type === 'hourly' ? t('laborTime.durationHelpHourly') : t('laborTime.durationHelpFixed')}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />

          {/* Quick select: pinned at the top of the work order's Add job dialog */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.is_quick_select}
                  onChange={(e) => setFormData((prev) => ({ ...prev, is_quick_select: e.target.checked }))}
                  data-testid="job-quick-select-checkbox"
                />
              }
              label={t('Show as quick select on work orders')}
            />
            <Typography sx={{ fontSize: '12px', color: 'text.secondary', ml: 4, mt: -0.5 }}>
              {t('quickSelectJobHelp')}
            </Typography>
          </Box>

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: '10px',
            px: 3,
          }}
        >
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          sx={{
            borderRadius: '10px',
            px: 3,
            bgcolor: 'secondary.main',
            '&:hover': { bgcolor: 'secondary.dark' },
          }}
        >
          {loading ? <span>{t('Saving...')}</span> : isEditMode ? <span>{t('Save Changes')}</span> : <span>{t('Add Job')}</span>}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withTranslation()(JobFormDialog);
